import React from 'react';
import { useMonthlyQuota } from '~/hooks/useMonthlyQuota';
import { usePlan } from '~/hooks/usePlan';
import { 
  ExclamationTriangleIcon, 
  CheckCircleIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { useI18n } from '~/i18n/i18n';

interface SidebarQuotaProps {
  userEmail?: string;
  shopDomain?: string;
}

export default function SidebarQuota({
  userEmail,
  shopDomain
}: SidebarQuotaProps) {
  const { locale } = useI18n();
  const isRTL = locale === 'he';
  
  
  const { 
    getPlanLimits, 
    isLoading: planLoading, 
    error: planError, 
    currentPlan,
    fetchUserPlan,
    planName: planNameFromHook
  } = usePlan({ userEmail, shopDomain });
  
  
  const planLimits = getPlanLimits();
  const monthlyLimit = planLimits.monthlyPlays === -1 ? Infinity : planLimits.monthlyPlays;
  
  
  const quotaData = useMonthlyQuota({
    userEmail,
    shopDomain,
    monthlyLimit
  });
  

  // Don't render if we don't have the required data
  if (!userEmail || !shopDomain) {
    return (
      <div className="px-4 lg:px-4 py-3 border-t border-[#f0f0f0]">
        <div className={`flex items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <ClockIcon className="w-4 h-4 text-[#6a6a6a]" />
            <span className="text-xs font-medium text-[#2c2c2c]">{isRTL ? 'מכסה חודשית' : 'Monthly Quota'}</span>
          </div>
          <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse"></div>
        </div>
        <div className="text-xs text-[#6a6a6a]">
          {!userEmail ? (isRTL ? 'טוען נתוני משתמש...' : 'Loading user data...') : (isRTL ? 'טוען נתוני חנות...' : 'Loading shop data...')}
        </div>
      </div>
    );
  }

  // Show loading state while data is being fetched
  if (quotaData.isLoading || planLoading) {
    return (
      <div className="px-4 lg:px-4 py-3 border-t border-[#f0f0f0]">
        <div className={`flex items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <ClockIcon className="w-4 h-4 text-[#6a6a6a]" />
            <span className="text-xs font-medium text-[#2c2c2c]">{isRTL ? 'מכסה חודשית' : 'Monthly Quota'}</span>
          </div>
          <div className="w-4 h-4 bg-gray-200 rounded-full animate-pulse"></div>
        </div>
        <div className="text-xs text-[#6a6a6a]">
          {planLoading ? (isRTL ? 'טוען נתוני תוכנית...' : 'Loading plan data...') : (isRTL ? 'טוען נתוני מכסה...' : 'Loading quota data...')}
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (quotaData.error || planError) {
    const errorMessage = planError || quotaData.error;
    const isPlanError = !!planError;
    
    return (
      <div className="px-4 lg:px-4 py-3 border-t border-[#f0f0f0]">
        <div className={`flex items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <ClockIcon className="w-4 h-4 text-[#6a6a6a]" />
            <span className="text-xs font-medium text-[#2c2c2c]">{isRTL ? 'מכסה חודשית' : 'Monthly Quota'}</span>
          </div>
          <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
        </div>
        <div className="text-xs text-red-600 mb-2">
          {isRTL ? 'שגיאה בטעינת' : 'Error loading'} {isPlanError ? (isRTL ? 'תוכנית' : 'plan') : (isRTL ? 'מכסה' : 'quota')}
        </div>
        <div className="text-xs text-[#6a6a6a] mb-2">
          {errorMessage?.includes('Failed to fetch') ? (isRTL ? 'שגיאת רשת - בדוק חיבור' : 'Network error - check connection') : 
           errorMessage?.includes('Client not found') ? (isRTL ? 'נתוני משתמש לא נמצאו' : 'User data not found') :
           (isRTL ? 'לא ניתן לטעון נתונים' : 'Unable to load data')}
        </div>
        <button 
          onClick={() => {
            fetchUserPlan();
          }}
          className="text-xs text-blue-600 hover:text-blue-800 underline"
        >
          {isRTL ? 'נסה שוב' : 'Retry'}
        </button>
      </div>
    );
  }

  const getStatusIcon = () => {
    if (monthlyLimit === Infinity) {
      return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
    }
    
    const percentage = (quotaData.currentUsage / quotaData.monthlyLimit) * 100;
    
    if (percentage >= 90) {
      return <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />;
    } else if (percentage >= 75) {
      return <ExclamationTriangleIcon className="w-4 h-4 text-orange-500" />;
    } else {
      return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
    }
  };

  const getStatusColor = () => {
    if (monthlyLimit === Infinity) {
      return 'text-green-600';
    }
    
    const percentage = (quotaData.currentUsage / quotaData.monthlyLimit) * 100;
    
    if (percentage >= 90) {
      return 'text-red-600';
    } else if (percentage >= 75) {
      return 'text-orange-600';
    } else {
      return 'text-green-600';
    }
  };

  const getProgressColor = () => {
    if (monthlyLimit === Infinity) {
      return 'bg-green-500';
    }
    
    const percentage = (quotaData.currentUsage / quotaData.monthlyLimit) * 100;
    
    if (percentage >= 90) {
      return 'bg-red-500';
    } else if (percentage >= 75) {
      return 'bg-orange-500';
    } else {
      return 'bg-green-500';
    }
  };

  return (
    <div className="px-4 lg:px-4 py-3 border-t border-[#f0f0f0]">
      {/* Quota Header */}
      <div className={`flex items-center justify-between mb-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <ClockIcon className="w-4 h-4 text-[#6a6a6a]" />
          <div>
            <span className="text-xs font-medium text-[#2c2c2c]">{isRTL ? 'מכסה חודשית' : 'Monthly Quota'}</span>
            {planNameFromHook && (
              <div className="text-xs text-[#6a6a6a]">{planNameFromHook} {isRTL ? 'תוכנית' : 'Plan'}</div>
            )}
          </div>
        </div>
        {getStatusIcon()}
      </div>

      {/* Usage Display */}
      <div className="mb-2">
        <div className={`flex items-center justify-between mb-1 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <span className="text-xs text-[#6a6a6a]">{isRTL ? 'משחקים' : 'Games'}</span>
          <span className={`text-xs font-semibold ${getStatusColor()}`}>
            {quotaData.currentUsage}
            {monthlyLimit === Infinity ? '' : `/${monthlyLimit}`}
            {monthlyLimit === Infinity && <span className={`text-green-600 ${isRTL ? 'mr-1' : 'ml-1'}`}>(∞)</span>}
          </span>
        </div>
        
        {/* Progress Bar */}
        {monthlyLimit === Infinity ? (
          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
            <div className="h-full rounded-full transition-all duration-300 bg-green-500" />
          </div>
        ) : (
          <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all duration-300 ${getProgressColor()}`}
              style={{ width: `${Math.min((quotaData.currentUsage / quotaData.monthlyLimit) * 100, 100)}%` }}
            />
          </div>
        )}
      </div>

      {/* Reset Info */}
      <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
        <span className="text-xs text-[#6a6a6a]">
          {quotaData.remainingDays <= 0 ? (isRTL ? 'מתאפס היום' : 'Resets today') :
           quotaData.remainingDays === 1 ? (isRTL ? 'מתאפס מחר' : 'Resets tomorrow') :
           (isRTL ? `מתאפס בעוד ${quotaData.remainingDays} ימים` : `Resets in ${quotaData.remainingDays}d`)}
        </span>
        <span className={`text-xs font-medium ${getStatusColor()}`}>
          {monthlyLimit === Infinity ? '∞' : `${Math.min((quotaData.currentUsage / quotaData.monthlyLimit) * 100, 100).toFixed(0)}%`}
        </span>
      </div>
    </div>
  );
}
